import React from 'react';

import { icons } from '../../themes/icons';

// Single source of truth for the sidebar entries, shared by the desktop Sidebar
// and the MobileSidebar so the two menus never drift apart again.

export type BadgeVariant = 'default' | 'offer' | 'agencies';

export interface MenuItem {
  label: string;
  route: string;
  icon: React.FC;
  exact?: boolean;
  /** Pending count shown as a badge on the icon (hidden when undefined or 0). */
  count?: number;
  /** Which badge offset to use on desktop, where the icons have different shapes. */
  badgeVariant?: BadgeVariant;
}

export interface MenuGroup {
  heading: string;
  tabs: MenuItem[];
}

export interface MenuCounts {
  supportCount?: number;
  offerPendingCount?: number;
  pendingAgenciesCount?: number;
  unreadChatCount?: number;
  fastCheckPendingCount?: number;
}

const UserResearchIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    {/* Body / shoulders */}
    <path d="M12 14c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    {/* Head: circle outline only — filled nodes inside create the "brain" */}
    <circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
    {/* Neural-network nodes */}
    <circle cx="12" cy="8" r="1.2" />
    <circle cx="12" cy="5.2" r="0.85" />
    <circle cx="9.6" cy="9.8" r="0.85" />
    <circle cx="14.4" cy="9.8" r="0.85" />
    {/* Connections between centre and satellite nodes */}
    <line x1="12" y1="6.8" x2="12" y2="6.05" stroke="currentColor" strokeWidth="0.8" />
    <line x1="11.1" y1="8.7" x2="10.2" y2="9.3" stroke="currentColor" strokeWidth="0.8" />
    <line x1="12.9" y1="8.7" x2="13.8" y2="9.3" stroke="currentColor" strokeWidth="0.8" />
  </svg>
);

// Three people linked by a referral (design asset Referral.svg).
const ReferralIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 94 94" fill="currentColor" aria-hidden="true">
    <path d="M73.4381 53.125C71.8131 53.125 70.235 53.3125 68.7193 53.6875L61.5318 41.7035L61.813 41.4691C61.8912 41.4066 61.9849 41.3441 62.063 41.266C67.1099 36.9691 70.313 30.563 70.313 23.438C70.313 10.516 59.7971 0 46.875 0C33.953 0 23.437 10.516 23.437 23.438C23.437 30.563 26.6402 36.969 31.6871 41.266C31.7652 41.3441 31.8589 41.4066 31.9371 41.4691L32.2183 41.7035L25.0308 53.6875C23.5152 53.3125 21.937 53.125 20.312 53.125C9.109 53.125 0 62.2344 0 73.437C0 80.1401 3.2656 86.093 8.2656 89.781L8.28123 89.7966C8.3906 89.8747 8.48435 89.9372 8.57811 90.0154C11.8906 92.3592 15.9531 93.7498 20.3121 93.7498C24.6711 93.7498 28.734 92.3592 32.0461 90.0154C32.1399 89.9372 32.2336 89.8748 32.343 89.7966L32.3586 89.781C36.968 86.3748 40.1086 81.0622 40.5617 75H53.2027C53.6402 81.0625 56.7808 86.375 61.3902 89.781C61.484 89.8591 61.5933 89.9529 61.7027 90.0154C65.0152 92.3592 69.0777 93.7498 73.4367 93.7498C77.7957 93.7498 81.8586 92.3592 85.1707 90.0154C85.2801 89.9529 85.3895 89.8591 85.4832 89.781C90.4832 86.0935 93.7488 80.1404 93.7488 73.437C93.7488 62.234 84.6394 53.125 73.4368 53.125H73.4381ZM31.2501 32.813V36.4068C28.3282 32.8912 26.5626 28.3599 26.5626 23.4378C26.5626 12.2348 35.672 3.1258 46.8746 3.1258C58.0772 3.1258 67.1866 12.2352 67.1866 23.4378C67.1866 28.3597 65.421 32.8909 62.4991 36.4068V32.813C62.4991 27.6411 58.296 23.438 53.1241 23.438C54.1085 22.1411 54.6866 20.5161 54.6866 18.7505V15.6255C54.6866 11.313 51.1866 7.813 46.8741 7.813C42.5616 7.813 39.0616 11.313 39.0616 15.6255V18.7505C39.0616 20.5161 39.6397 22.1411 40.6241 23.438C35.4522 23.438 31.2501 27.6411 31.2501 32.813ZM32.8126 85.235V82.8131C32.8126 78.3756 29.7188 74.6412 25.5626 73.6881C26.2032 72.7037 26.5626 71.5475 26.5626 70.3131V67.1881C26.5626 63.735 23.7657 60.9381 20.3126 60.9381C16.8595 60.9381 14.0626 63.735 14.0626 67.1881V70.3131C14.0626 71.5475 14.422 72.7037 15.0626 73.6881C10.9064 74.6412 7.8126 78.3756 7.8126 82.8131V85.235C4.9064 82.1412 3.1251 78.0006 3.1251 73.438C3.1251 63.9536 10.8282 56.25 20.3131 56.25C22.0006 56.25 23.6256 56.4844 25.1569 56.9375C25.2194 56.9688 25.2819 56.9844 25.3444 57C25.36 57.0156 25.3913 57.0156 25.4225 57.0156C32.4225 59.2031 37.5005 65.7344 37.5005 73.4376C37.5005 78.0001 35.7189 82.1411 32.8126 85.235ZM40.5626 71.876C39.9688 64.1104 34.9845 57.532 28.0936 54.673L34.7811 43.517C38.3123 45.642 42.453 46.8764 46.8751 46.8764C51.2972 46.8764 55.4376 45.642 58.9691 43.517L65.6566 54.673C58.766 57.5324 53.7816 64.1105 53.2036 71.876H40.5626ZM85.9376 85.235V82.8131C85.9376 78.3756 82.8438 74.6412 78.6876 73.6881C79.3282 72.7037 79.6876 71.5475 79.6876 70.3131V67.1881C79.6876 63.735 76.8907 60.9381 73.4376 60.9381C69.9845 60.9381 67.1876 63.735 67.1876 67.1881V70.3131C67.1876 71.5475 67.547 72.7037 68.1876 73.6881C64.0314 74.6412 60.9376 78.3756 60.9376 82.8131V85.235C58.0314 82.1412 56.2501 78.0006 56.2501 73.438C56.2501 65.7349 61.3282 59.204 68.3281 57.016C68.3594 57.016 68.3906 57.016 68.4062 57.0004C68.4687 56.9847 68.5312 56.9691 68.5937 56.9379C70.1249 56.4848 71.7499 56.2504 73.4375 56.2504C82.9219 56.2504 90.6255 63.9535 90.6255 73.4384C90.6255 78.0009 88.8439 82.1411 85.9376 85.235Z" />
  </svg>
);

const dashboardItem: MenuItem = {
  label: 'Dashboard',
  route: '/',
  icon: icons.DashboardIcon,
  exact: true,
};
const eventsItem: MenuItem = { label: 'Events', route: '/event', icon: icons.CalendarTodayIcon };
const referralsItem: MenuItem = {
  label: 'Referrals',
  route: '/referrals',
  icon: ReferralIcon,
};
const campaignsItem: MenuItem = {
  label: 'Campaigns',
  route: '/campaigns',
  icon: icons.NotificationsActiveIcon,
};

const chatItem = (unreadChatCount?: number): MenuItem => ({
  label: 'Chat',
  route: '/chats',
  icon: icons.ChatIcon,
  count: unreadChatCount,
  badgeVariant: 'default',
});
const profileUpdatesItem = (supportCount?: number): MenuItem => ({
  label: 'Profile updates',
  route: '/profile-updates',
  icon: icons.SupportIcon,
  count: supportCount,
  badgeVariant: 'default',
});
const fastCheckItem = (fastCheckPendingCount?: number): MenuItem => ({
  label: 'Fast Check',
  route: '/fast-check-reservations',
  icon: icons.FastForward,
  count: fastCheckPendingCount,
  badgeVariant: 'default',
});

export const menuVenue = (venueId?: string): MenuGroup[] => [
  {
    heading: 'Main',
    tabs: [
      {
        label: 'Businesses',
        route: `/businesses/${venueId}/profile`,
        icon: icons.StorefrontIcon,
      },
      { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon },
      { label: 'Reservations', route: '/reservations', icon: icons.EventAvailableIcon },
    ],
  },
];

export const menuItemsAdmin = (
  {
    supportCount,
    offerPendingCount,
    pendingAgenciesCount,
    unreadChatCount,
    fastCheckPendingCount,
  }: MenuCounts,
  { superAdmin }: { superAdmin: boolean },
): MenuGroup[] => [
  {
    heading: 'Main',
    tabs: [
      dashboardItem,
      {
        label: 'Model Applications',
        route: '/user-applications',
        icon: icons.ModelIcon,
        exact: true,
      },
      {
        label: 'Business Applications',
        route: '/business-applications',
        icon: icons.VenueIcon,
        exact: true,
      },
      {
        label: 'Offer Applications',
        route: '/offer-applications',
        icon: icons.OfferIcon,
        exact: true,
        count: offerPendingCount,
        badgeVariant: 'offer',
      },
      referralsItem,
    ],
  },
  {
    heading: 'Support',
    tabs: [
      chatItem(unreadChatCount),
      profileUpdatesItem(supportCount),
      fastCheckItem(fastCheckPendingCount),
      { label: 'Support AI', route: '/support-ai', icon: UserResearchIcon },
    ],
  },
  {
    heading: 'Database',
    tabs: [
      { label: 'Models', route: '/users', icon: icons.PeopleIcon },
      { label: 'Leaderboard', route: '/leaderboard', icon: icons.EqualizerIcon },
      { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon },
      { label: 'Subscriptions', route: '/subscriptions', icon: icons.CardGiftcardIcon },
      { label: 'Markets', route: '/markets', icon: icons.LocationCityIcon },
      { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon },
      eventsItem,
      { label: 'Content', route: '/content', icon: icons.CollectionsIcon },
      {
        label: 'Agencies',
        route: '/agencies',
        icon: icons.SheildIcon,
        count: pendingAgenciesCount,
        badgeVariant: 'agencies',
      },
    ],
  },
  {
    heading: 'Core Action',
    tabs: [
      { label: 'Reservations', route: '/reservations', icon: icons.EventAvailableIcon },
      { label: 'Collabs', route: '/collabs', icon: icons.EmailIcon },
      { label: 'Scouting', route: '/scouting', icon: icons.ScoutingIcon },
      { label: 'No Shows', route: '/no-shows', icon: icons.CancelIcon },
      { label: 'Penalties', route: '/penalties', icon: icons.WarningIcon },
      { label: 'Suspensions', route: '/suspensions', icon: icons.BlockIcon },
    ],
  },
  {
    heading: 'Admin',
    tabs: [
      campaignsItem,
      ...(superAdmin
        ? [
            { label: 'Staff', route: '/staff', icon: icons.FaceIcon },
            {
              label: 'Terms & Conditions',
              route: '/terms-and-conditions',
              icon: icons.AssignmentIcon,
            },
            { label: 'Logs', route: '/logs', icon: icons.ViewStreamIcon },
            { label: 'SQL Console', route: '/sql-console', icon: icons.AssignmentIcon },
          ]
        : []),
      { label: 'Instagram Accounts', route: '/instagram-accounts', icon: icons.PeopleIcon },
      { label: 'Search Businesses', route: '/search-businesses', icon: icons.SearchIconV2 },
      { label: 'DAC7', route: '/dac7', icon: icons.ReceiptIcon },
    ],
  },
];

export const menuItemsSupport = ({
  supportCount,
  unreadChatCount,
  fastCheckPendingCount,
}: MenuCounts): MenuGroup[] => [
  { heading: 'Main', tabs: [dashboardItem, eventsItem] },
  {
    heading: 'Support',
    tabs: [
      chatItem(unreadChatCount),
      profileUpdatesItem(supportCount),
      fastCheckItem(fastCheckPendingCount),
    ],
  },
];

export const menuItemsFastCheck = ({ fastCheckPendingCount }: MenuCounts): MenuGroup[] => [
  { heading: 'Main', tabs: [dashboardItem, eventsItem, fastCheckItem(fastCheckPendingCount)] },
];

export const menuItemsSocial: MenuGroup[] = [
  {
    heading: 'Main',
    tabs: [{ label: 'Content', route: '/content', icon: icons.CollectionsIcon }, eventsItem],
  },
];

export const menuItemsCityManager = ({ unreadChatCount }: MenuCounts): MenuGroup[] => [
  { heading: 'Main', tabs: [dashboardItem, referralsItem] },
  { heading: 'Support', tabs: [chatItem(unreadChatCount)] },
  {
    heading: 'Database',
    tabs: [
      {
        label: 'Business Applications',
        route: '/business-applications',
        icon: icons.StorefrontIcon,
        exact: true,
      },
      {
        label: 'Offer Applications',
        route: '/offer-applications',
        icon: icons.OfferIcon,
        exact: true,
      },
      { label: 'Markets', route: '/markets', icon: icons.LocationCityIcon },
      { label: 'Models', route: '/users', icon: icons.PeopleIcon },
      { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon },
      { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon },
      eventsItem,
      campaignsItem,
    ],
  },
];
